import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Filter, RefreshCw, ArrowDown } from 'lucide-react';
import GrowthTabNav from './GrowthTabNav';
import GrowthDateFilter from './GrowthDateFilter';
import { getGrowthFunnel } from '../../Services/GrowthService';
import { GrowthFunnel } from '../../Types/GrowthType';

function GrowthFunnelComponent() {
  const [data, setData] = useState<GrowthFunnel | null>(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [appliedFrom, setAppliedFrom] = useState('');
  const [appliedTo, setAppliedTo] = useState('');
  const [role, setRole] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getGrowthFunnel({
        fromDate: appliedFrom || undefined,
        toDate: appliedTo || undefined,
        role: role || undefined,
      });
      if (response.success && response.data) {
        setData(response.data);
      }
    } catch (err) {
      console.error('Failed to load growth funnel', err);
    } finally {
      setLoading(false);
    }
  }, [appliedFrom, appliedTo, role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const maxCount = useMemo(
    () => Math.max(1, ...(data?.stages || []).map((s) => s.count)),
    [data]
  );

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      <GrowthTabNav />

      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
          <Filter size={22} />
        </span>
        <div>
          <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">Acquisition Funnel</h1>
          <p className="text-xs font-semibold text-slate-400 md:text-sm">
            Visitor → Registration → Approval conversion stages
          </p>
        </div>
        <button
          onClick={fetchData}
          title="Refresh"
          className="ml-auto inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-sm font-bold text-slate-200 transition hover:border-cyan-500/40 hover:text-cyan-400"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-3 xl:grid-cols-2">
        <GrowthDateFilter
          fromDate={fromDate}
          toDate={toDate}
          onApply={(f, t) => {
            setFromDate(f);
            setToDate(t);
            setAppliedFrom(f);
            setAppliedTo(t);
          }}
          extra={
            <div className="flex items-center gap-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Role</label>
              <select value={role} onChange={(e) => setRole(e.target.value)} className="rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2 text-xs font-semibold text-slate-100 outline-none focus:border-cyan-500/50 [color-scheme:dark]">
                <option value="">All roles</option>
                <option value="CANDIDATE">Candidate</option>
                <option value="RECRUITER">Recruiter</option>
              </select>
            </div>
          }
        />
      </div>

      {loading && !data ? (
        <div className="flex min-h-[50vh] items-center justify-center text-slate-400">
          <RefreshCw className="h-10 w-10 animate-spin text-cyan-500" />
        </div>
      ) : !data || data.stages.length === 0 ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-16 text-center text-sm font-semibold text-slate-500">
          No funnel data for this range.
        </div>
      ) : (
        <div className="mx-auto max-w-3xl">
          {data.stages.map((stage, idx) => (
            <div key={stage.name}>
              <motion.div
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.08 }}
                className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5"
              >
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div>
                    <span className="mr-2 rounded-lg bg-gradient-to-r from-cyan-500 to-teal-500 px-2 py-0.5 text-[10px] font-black uppercase text-slate-950">
                      {idx + 1}
                    </span>
                    <span className="text-sm font-black uppercase tracking-widest text-slate-200">{stage.name}</span>
                  </div>
                  <span className="text-xl font-black text-slate-100">{stage.count.toLocaleString()}</span>
                </div>
                <div className="h-3 overflow-hidden rounded-full bg-slate-800">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 transition-all duration-700"
                    style={{ width: `${Math.max(2, (stage.count / maxCount) * 100)}%` }}
                  />
                </div>
                <p className="mt-2 text-[11px] font-semibold text-slate-500">
                  {stage.conversionPct.toFixed(1)}% of previous stage
                </p>
              </motion.div>
              {idx < data.stages.length - 1 && (
                <div className="flex justify-center py-1">
                  <ArrowDown className="h-4 w-4 text-slate-600" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default GrowthFunnelComponent;